/*
Description: This is the file that will handle interacting with the Practice Perfect EMR (app.practiceperfectemr.com)
Date: 5/30/2024
Author: Jacob Michalski

Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

export function getPageInfo() {
  // function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "PT Daily/SOAP Note",...
          }
      */
  let noteType = '';
  let noteTypeSelector = document.querySelector(
    '#app > div.app-header.navbar > div.collapse.pos-rlt.navbar-collapse.box-shadow.bg-dark.dk > div:nth-child(3) > div:nth-child(1) > div > h5 > strong'
  );
  if (noteTypeSelector?.innerText === 'PROGRESS NOTES') {
    noteType = 'PT Daily';
  } else if (noteTypeSelector?.innerText === 'DOCUMENTS') {
    const documentTypeSelector = document.querySelector(
      '#app > div.app-content > div > div.hbox.hbox-auto-xs.bg-light > div:nth-child(2) > div > div > div > div > div:nth-child(1) > div.panel-heading.lter.b-b.selected > b'
    );
    noteType = documentTypeSelector?.textContent.trim();
  }
  const baseURL = window.location.origin;
  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPatientInfo() {
  // function getPatientInfo() {
  /*
      Description: This function will get the patient info from the note
      Return: A dictionary with the patient info
      {
          "EMR ID": 0x1234 or "michalskij"
          "First Name":firstName,
          "Last Name": lastName,
          "DOB": dob,
      }
      */
  // Assuming the element's text is "Halper John, he/him (EMR ID: 42332797)"
  let patientName = document.querySelector(
    '#app > div.app-header.navbar > div.collapse.pos-rlt.navbar-collapse.box-shadow.bg-dark.dk > div:nth-child(3) > div:nth-child(2) > div'
  );

  const fullName = patientName.innerHTML.split(',');

  // Extract and trim the last name and first name
  const lastName = fullName[0].trim();
  const firstName = fullName[1].trim();
  const emrID = `${lastName},${firstName}`.replace(/\s/g, '');
  const dob = '05/29/2023';
  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]
 
*/
var questions = [];
var questionInteractions = {};

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Short Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/

export async function getQuestions() {
  questions = [];
  questionInteractions = {};
  const pageDetails = getPageInfo();
  if (pageDetails['Note Type'] === 'PT Daily') {
    return getQuestionsForPTDaily();
  } else {
    return getQuestionsForDocument();
  }
}

export async function getQuestionsForPTDaily() {
  questions = [];
  questionInteractions = {};

  const dispatchEditorEvents = (el) => {
    if (!el) return;
    ['beforeinput', 'input', 'change', 'keyup', 'blur'].forEach((type) => {
      try {
        el.dispatchEvent(new Event(type, { bubbles: true }));
      } catch (e) {}
    });
  };

  // Robust: editor is usually sibling of the title's PARENT (header row), not the title itself
  const findEditorForTitleEl = (titleEl) => {
    // Try a few ancestor levels (title -> header row -> panel heading, etc.)
    const startNodes = [];
    let cur = titleEl;
    for (let i = 0; i < 6 && cur; i++) {
      startNodes.push(cur);
      cur = cur.parentElement;
    }

    for (const start of startNodes) {
      let el = start;

      // Walk forward through siblings
      for (let i = 0; i < 25; i++) {
        el = el?.nextElementSibling;
        if (!el) break;

        // Direct contenteditable
        if (el.getAttribute?.('contenteditable') === 'true') return el;

        // Nested contenteditable
        const nested = el.querySelector?.('[contenteditable="true"]');
        if (nested) return nested;
      }
    }

    return null;
  };

  let sections = document.querySelectorAll('.editorTitle');

  sections.forEach((sectionTitleElement) => {
    const sectionName = sectionTitleElement.textContent.trim();
    const editorEl = findEditorForTitleEl(sectionTitleElement);

    if (!editorEl) {
      console.warn(
        '[PTDaily] No editor found for section:',
        sectionName,
        sectionTitleElement
      );
      return;
    }

    questionInteractions[sectionName] = {
      Type: 'Long Answer',
      Question: sectionName,
      Key: sectionName,
      ExistingNote: (editorEl.innerText || '').trim(),
      Input: (val, replyType) => {
        if (isEmptyOrNull(val)) {
          return;
        }
        editorEl.focus();

        if (replyType === 'append') {
          const current = (editorEl.innerText || '').trim();
          editorEl.innerText =
            (current ? current + '\n' : '') + String(val).trim();
        } else {
          editorEl.innerText = String(val).trim();
        }

        dispatchEditorEvents(editorEl);
      }
    };
  });

  // Fill the questions array with the questions
  Object.keys(questionInteractions).forEach((key) => {
    questions.push(questionInteractions[key]);
  });

  return questions;
}

export async function getQuestionsForDocument() {
  // Select all sections dynamically
  let panels = document.querySelectorAll('.panel-default');
  panels.forEach((panel) => {
    const sections = panel.querySelectorAll('.list-inline');
    sections.forEach((section) => {
      // Find the label text for the textarea
      const labelElement = section.querySelector('span');
      let sectionName = '';

      // Fetch the correct text node that contains the label text
      labelElement?.childNodes?.forEach((node) => {
        if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.trim() !== ''
        ) {
          sectionName = node.textContent.trim();
        }
      });

      let sectionContentElement = section.querySelector('textarea');
      if (sectionContentElement) {
        createQuestionInteraction(sectionName, sectionContentElement);
      }
    });

    const panelHeadingElement = panel.querySelector('.panel-heading span');
    let panelHeading = panelHeadingElement?.innerText.trim() || '';

    if (panelHeading && panelHeading.length > 0) {
      const inputElements = panel.querySelectorAll(
        'input[type="text"].form-control[style="width: 200px;"]'
      );

      inputElements.forEach((inputElement) => {
        if (!questionInteractions[panelHeading]) {
          createQuestionInteraction(panelHeading, inputElement);
        }
      });
    } else {
      handlePanelWithoutHeading(panel);
    }
  });

  fillQuestionsArray();
  console.log(questions);
  return questions;
}

function createQuestionInteraction(sectionName, element) {
  const sanitizedSectionName = sectionName.replace(/:/g, '').trim();
  questionInteractions[sanitizedSectionName] = {
    Type: 'Long Answer',
    Question: sanitizedSectionName,
    Key: sanitizedSectionName,
    ExistingNote: element.value.trim(),
    Input: (val, replyType) => {
      if (isEmptyOrNull(val)) {
        return;
      }
      element.value =
        replyType === 'append' ? (element.value + '\n' + val).trim() : val;
      element.dispatchEvent(new Event('blur'));
    }
  };
}

function handlePanelWithoutHeading(panel) {
  const wrapperElement = panel.querySelector('.wrapper');

  if (wrapperElement) {
    const rowWrapperElement = wrapperElement.querySelector('.row.wrapper');

    if (rowWrapperElement) {
      const listInlineElements =
        rowWrapperElement.querySelectorAll('.list-inline');

      listInlineElements.forEach((listInlineElement, index) => {
        const tableHeaderElement = listInlineElement.querySelector(
          'table.table-striped.table-bordered.table-condensed thead th:first-child'
        );

        if (tableHeaderElement) {
          let sectionName = tableHeaderElement.innerText.trim();

          if (sectionName) {
            const nextInlineElement = listInlineElements[index + 1];
            if (nextInlineElement) {
              const correspondingElement = nextInlineElement.querySelector(
                'textarea, input[type="text"].form-control'
              );

              if (correspondingElement) {
                createQuestionInteraction(sectionName, correspondingElement);
              }
            }
          }
        }
      });
    }
  }
}

function fillQuestionsArray() {
  Object.keys(questionInteractions).forEach((key) => {
    questions.push(questionInteractions[key]);
  });
  console.log(questions);
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export function answerQuestions(answers) {
  // function answerQuestions(answers) {
  /*
      Description: This function will take the answers and populate the questions
      
      Args: 
          answers: A dictionary of answers
              "Question": "The question in question"
              "Answer": "The answer to the question"
     
      Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
          [
              {
                  Question: "Where was the injury?", 
                  Answer: "Back" 
                  Error: "The error message" (Choice not found, etc)
              },
              ...
          }
  
      */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(answer['Question'], answer['Answer']);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export function answerQuestion(question, answer, replyType) {
  // function answerQuestion(question, answer) {
  /*
      Description: This function will take the answer and populate the question
      
      Args: 
          question: The question in question
          answer: The answer to the question
     
      Return: null if success otherwise a string with the error message
      */

  // console.log("Answering Question: '" + question + "' with '" + answer + "'");

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions);
    // console.log(Object.keys(questionInteractions));

    return 'Question not found';
  }

  // If it is a short answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.error('Question: ' + question);
      console.error('Answer:' + answer);
      console.error(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
